/**
 * Shared drill-down components for financial statement views.
 * Used by TrialBalance, BalanceSheet, and IncomeStatement.
 */
import React from 'react';
import { FINANCIAL_API, authFetch } from '../constants';

const fmt = (n: number) =>
  new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);

// ── State hook ────────────────────────────────────────────────

export function useDrillDown() {
  const [expandedAcct, setExpandedAcct] = React.useState<string | null>(null);
  const [acctDetail, setAcctDetail] = React.useState<any>(null);
  const [acctLoading, setAcctLoading] = React.useState(false);
  const [expandedJE, setExpandedJE] = React.useState<number | null>(null);
  const [jeDetail, setJeDetail] = React.useState<any>(null);
  const [jeLoading, setJeLoading] = React.useState(false);

  const resetDrill = () => {
    setExpandedAcct(null);
    setAcctDetail(null);
    setExpandedJE(null);
    setJeDetail(null);
  };

  const toggleAccount = (acctCode: string, entity: string, period: string, view: string, isConsolidated: boolean) => {
    if (expandedAcct === acctCode) {
      setExpandedAcct(null);
      setAcctDetail(null);
      setExpandedJE(null);
      setJeDetail(null);
      return;
    }
    setExpandedAcct(acctCode);
    setExpandedJE(null);
    setJeDetail(null);
    setAcctLoading(true);

    const url = isConsolidated
      ? `${FINANCIAL_API}/consolidated/account-breakdown?account_code=${acctCode}&period=${period}&view=${view}`
      : `${FINANCIAL_API}/account-detail?entity_code=${entity}&account_code=${acctCode}&period=${period}&view=${view}`;

    authFetch(url)
      .then(r => r.json())
      .then(d => { setAcctDetail(d); setAcctLoading(false); })
      .catch(() => setAcctLoading(false));
  };

  const toggleJE = (jeId: number) => {
    if (expandedJE === jeId) {
      setExpandedJE(null);
      setJeDetail(null);
      return;
    }
    setExpandedJE(jeId);
    setJeLoading(true);
    authFetch(`${FINANCIAL_API}/journal-entry/${jeId}`)
      .then(r => r.json())
      .then(d => { setJeDetail(d); setJeLoading(false); })
      .catch(() => setJeLoading(false));
  };

  return {
    expandedAcct, acctDetail, acctLoading,
    expandedJE, jeDetail, jeLoading,
    resetDrill, toggleAccount, toggleJE,
  };
}

// ── JE Detail panel ──────────────────────────────────────────

export function JEDetailPanel({ je }: { je: any }) {
  return (
    <div style={{ padding: '8px 24px 12px', background: 'rgba(6,182,212,0.02)', borderTop: '1px solid var(--accent-subtle)' }}>
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 8, fontSize: 11, color: 'var(--text-dim)' }}>
        <span><b>JE:</b> {je.je_number}</span>
        <span><b>Date:</b> {je.posting_date}</span>
        <span><b>Entity:</b> {je.entity_code}</span>
        <span><b>Type:</b> {je.je_type}</span>
        {je.source_type && <span><b>Source:</b> {je.source_type}</span>}
        <span><b>Status:</b> {je.status}</span>
        <span style={{ color: je.is_balanced ? 'var(--accent-good)' : 'var(--accent-critical)' }}>
          {je.is_balanced ? 'Balanced' : 'IMBALANCED'}
        </span>
      </div>
      {je.description && (
        <div style={{ fontSize: 11, color: 'var(--text-dim)', marginBottom: 8 }}>{je.description}</div>
      )}
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
        <thead>
          <tr style={{ borderBottom: '1px solid var(--bg-border)' }}>
            <th style={thSub}>#</th>
            <th style={thSub}>Account</th>
            <th style={thSub}>Description</th>
            <th style={{ ...thSub, textAlign: 'right' }}>Debit</th>
            <th style={{ ...thSub, textAlign: 'right' }}>Credit</th>
          </tr>
        </thead>
        <tbody>
          {je.lines.map((ln: any) => (
            <tr key={ln.line_number} style={{ borderBottom: '1px solid rgba(0,0,0,0.02)' }}>
              <td style={{ ...tdSub, color: 'var(--text-dimmer)', width: 30 }}>{ln.line_number}</td>
              <td style={{ ...tdSub, fontFamily: 'monospace' }}>
                {ln.account_code} <span style={{ color: 'var(--text-dimmer)' }}>{ln.account_name}</span>
              </td>
              <td style={tdSub}>{ln.description}</td>
              <td style={{ ...tdSub, textAlign: 'right', fontFamily: 'monospace' }}>
                {ln.debit > 0 ? fmt(ln.debit) : ''}
              </td>
              <td style={{ ...tdSub, textAlign: 'right', fontFamily: 'monospace' }}>
                {ln.credit > 0 ? fmt(ln.credit) : ''}
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr style={{ borderTop: '1px solid var(--bg-border)' }}>
            <td colSpan={3} style={{ ...tdSub, fontWeight: 600 }}>Total</td>
            <td style={{ ...tdSub, textAlign: 'right', fontFamily: 'monospace', fontWeight: 600 }}>{fmt(je.total_debits)}</td>
            <td style={{ ...tdSub, textAlign: 'right', fontFamily: 'monospace', fontWeight: 600 }}>{fmt(je.total_credits)}</td>
          </tr>
        </tfoot>
      </table>

      {je.exception_link && (
        <div style={{
          marginTop: 8, padding: '8px 12px', borderRadius: 6,
          background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.2)', fontSize: 11,
        }}>
          <div style={{ fontWeight: 700, color: '#f59e0b', marginBottom: 4 }}>
            Source Exception #{je.exception_link.exception_id}
          </div>
          <div style={{ color: 'var(--text-dim)' }}>
            <b>Type:</b> {je.exception_link.type} &middot;
            <b> Status:</b> {je.exception_link.status} &middot;
            <b> Amount:</b> {je.exception_link.amount != null ? `$${fmt(je.exception_link.amount)}` : 'N/A'} &middot;
            <b> Dept:</b> {je.exception_link.department}
          </div>
          {je.exception_link.description && (
            <div style={{ color: 'var(--text-dimmer)', marginTop: 4, lineHeight: 1.4 }}>
              {je.exception_link.description.length > 200
                ? je.exception_link.description.slice(0, 200) + '...'
                : je.exception_link.description}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Account Lines (entity view) ──────────────────────────────

export function AccountLinesPanel({
  detail, expandedJE, jeDetail, jeLoading, toggleJE,
}: {
  detail: any;
  expandedJE: number | null;
  jeDetail: any;
  jeLoading: boolean;
  toggleJE: (id: number) => void;
}) {
  if (!detail.lines || detail.lines.length === 0) {
    return <div style={{ padding: '12px 24px', fontSize: 12, color: 'var(--text-dimmer)' }}>No journal entries for this account.</div>;
  }
  return (
    <div style={{ padding: '8px 16px' }}>
      <div style={{ fontSize: 11, color: 'var(--text-dimmer)', marginBottom: 6, fontWeight: 600 }}>
        {detail.line_count} journal entry line{detail.line_count !== 1 ? 's' : ''} &middot;
        Debits: {fmt(detail.total_debits)} &middot; Credits: {fmt(detail.total_credits)}
      </div>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
        <thead>
          <tr style={{ borderBottom: '1px solid var(--bg-border)' }}>
            <th style={thSub}>Date</th>
            <th style={thSub}>JE #</th>
            <th style={thSub}>Description</th>
            <th style={thSub}>Type</th>
            <th style={{ ...thSub, textAlign: 'right' }}>Debit</th>
            <th style={{ ...thSub, textAlign: 'right' }}>Credit</th>
          </tr>
        </thead>
        <tbody>
          {detail.lines.map((l: any, i: number) => (
            <React.Fragment key={i}>
              <tr
                onClick={() => toggleJE(l.je_id)}
                style={{
                  borderBottom: '1px solid rgba(0,0,0,0.03)',
                  cursor: 'pointer',
                  background: expandedJE === l.je_id ? 'rgba(6,182,212,0.06)' : 'transparent',
                }}
                onMouseEnter={e => { if (expandedJE !== l.je_id) (e.currentTarget as HTMLElement).style.background = 'rgba(0,0,0,0.02)'; }}
                onMouseLeave={e => { if (expandedJE !== l.je_id) (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
              >
                <td style={tdSub}>{l.posting_date}</td>
                <td style={{ ...tdSub, fontFamily: 'monospace', color: 'var(--accent)' }}>
                  <span style={{ fontSize: 9, marginRight: 4 }}>{expandedJE === l.je_id ? '\u25BC' : '\u25B6'}</span>
                  {l.je_number}
                </td>
                <td style={{ ...tdSub, color: 'var(--text-base)', maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {l.line_description || l.je_description}
                </td>
                <td style={tdSub}>
                  <span style={{
                    padding: '2px 6px', borderRadius: 4, fontSize: 10, fontWeight: 600,
                    background: l.je_type === 'ADJUSTMENT' ? '#fffbeb' : 'var(--accent-subtle)',
                    color: l.je_type === 'ADJUSTMENT' ? '#f59e0b' : 'var(--navy-700)',
                  }}>
                    {l.je_type}
                  </span>
                </td>
                <td style={{ ...tdSub, textAlign: 'right', fontFamily: 'monospace' }}>
                  {l.debit > 0 ? fmt(l.debit) : ''}
                </td>
                <td style={{ ...tdSub, textAlign: 'right', fontFamily: 'monospace' }}>
                  {l.credit > 0 ? fmt(l.credit) : ''}
                </td>
              </tr>
              {expandedJE === l.je_id && (
                <tr>
                  <td colSpan={6} style={{ padding: 0 }}>
                    {jeLoading ? (
                      <div style={{ padding: '8px 24px', fontSize: 11, color: 'var(--text-dimmer)' }}>Loading JE...</div>
                    ) : jeDetail ? (
                      <JEDetailPanel je={jeDetail} />
                    ) : null}
                  </td>
                </tr>
              )}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Consolidated Breakdown ───────────────────────────────────

export function ConsolidatedBreakdownPanel({ detail }: { detail: any }) {
  if (!detail.entities || detail.entities.length === 0) {
    return <div style={{ padding: '12px 24px', fontSize: 12, color: 'var(--text-dimmer)' }}>No entity data for this account.</div>;
  }
  return (
    <div style={{ padding: '8px 16px' }}>
      <div style={{ fontSize: 11, color: 'var(--text-dimmer)', marginBottom: 6, fontWeight: 600 }}>
        Entity breakdown for {detail.account_code} {detail.account_name}
      </div>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
        <thead>
          <tr style={{ borderBottom: '1px solid var(--bg-border)' }}>
            <th style={thSub}>Entity</th>
            <th style={thSub}>Currency</th>
            <th style={{ ...thSub, textAlign: 'right' }}>Local Balance</th>
            <th style={{ ...thSub, textAlign: 'right' }}>FX Rate</th>
            <th style={thSub}>Rate Type</th>
            <th style={{ ...thSub, textAlign: 'right' }}>USD Balance</th>
          </tr>
        </thead>
        <tbody>
          {detail.entities.map((e: any) => (
            <tr key={e.entity_code} style={{ borderBottom: '1px solid rgba(0,0,0,0.03)' }}>
              <td style={{ ...tdSub, color: 'var(--text-base)' }}>{e.entity_name}</td>
              <td style={tdSub}>{e.functional_currency}</td>
              <td style={{ ...tdSub, textAlign: 'right', fontFamily: 'monospace' }}>
                {e.local_balance < 0 ? `(${fmt(Math.abs(e.local_balance))})` : fmt(e.local_balance)}
              </td>
              <td style={{ ...tdSub, textAlign: 'right', fontFamily: 'monospace' }}>
                {e.fx_rate != null ? e.fx_rate.toFixed(5) : '-'}
              </td>
              <td style={tdSub}>{e.fx_rate_type}</td>
              <td style={{ ...tdSub, textAlign: 'right', fontFamily: 'monospace', fontWeight: 600 }}>
                {e.usd_balance != null
                  ? (e.usd_balance < 0 ? `(${fmt(Math.abs(e.usd_balance))})` : fmt(e.usd_balance))
                  : '-'}
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr style={{ borderTop: '1px solid var(--bg-border)' }}>
            <td colSpan={5} style={{ ...tdSub, fontWeight: 700 }}>Total (USD)</td>
            <td style={{ ...tdSub, textAlign: 'right', fontFamily: 'monospace', fontWeight: 700 }}>
              {detail.total_usd < 0 ? `(${fmt(Math.abs(detail.total_usd))})` : fmt(detail.total_usd)}
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

// ── Drill-down expansion row (wraps sub-panels) ─────────────

export function DrillDownRow({
  colSpan,
  isLoading,
  detail,
  isConsolidated,
  expandedJE,
  jeDetail,
  jeLoading,
  toggleJE,
}: {
  colSpan: number;
  isLoading: boolean;
  detail: any;
  isConsolidated: boolean;
  expandedJE: number | null;
  jeDetail: any;
  jeLoading: boolean;
  toggleJE: (id: number) => void;
}) {
  return (
    <tr>
      <td colSpan={colSpan} style={{ padding: 0 }}>
        <div style={{ background: 'rgba(6,182,212,0.03)', borderBottom: '1px solid var(--bg-border)' }}>
          {isLoading ? (
            <div style={{ padding: '12px 24px', fontSize: 12, color: 'var(--text-dimmer)' }}>Loading details...</div>
          ) : detail ? (
            isConsolidated
              ? <ConsolidatedBreakdownPanel detail={detail} />
              : <AccountLinesPanel detail={detail} expandedJE={expandedJE} jeDetail={jeDetail} jeLoading={jeLoading} toggleJE={toggleJE} />
          ) : null}
        </div>
      </td>
    </tr>
  );
}

// ── Styles ────────────────────────────────────────────────────

const thSub: React.CSSProperties = {
  padding: '4px 10px', textAlign: 'left', fontSize: 10,
  fontWeight: 600, color: 'var(--text-dimmer)', textTransform: 'uppercase',
};

const tdSub: React.CSSProperties = {
  padding: '4px 10px', color: 'var(--text-dim)', fontSize: 12,
};
